import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import '../button/lib-button';
import '../icon/lib-icon';
import notificationCss from './lib-inline-notification.css';

@customElement('lib-inline-notification')
export class LibInlineNotification extends LibElement {
  protected override get hostFocusSelector(): string {
    return this.actionText && !this.hasSlottedAction ? 'lib-button' : '';
  }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(notificationCss)];

  @property({ reflect: true }) severity = 'information';
  @property({ reflect: true }) surface = 'white';
  @property({ attribute: 'icon-alignment', reflect: true }) iconAlignment = 'center';
  @property({ attribute: 'icon-url' }) iconUrl = '';
  @property({ attribute: 'icon-name' }) iconName = '';
  @property({ type: Boolean }) spinning = false;
  @property() headline = '';
  @property() text = '';
  @property({ attribute: 'action-text' }) actionText = '';
  @property({ attribute: 'action-variant' }) actionVariant = 'tertiary';
  @property({ attribute: 'action-icon-trailing', type: Boolean }) actionIconTrailing = false;

  @state() private hasSlottedAction = false;

  override render() {
    const severityIcons: Record<string, string> = { success: 'circle-tick-sign', information: 'information_circle' };
    const icon = this.iconName || severityIcons[this.severity] || 'warning';
    const actionIcon = html`<lib-icon aria-hidden="true" icon-url=${new URL('../wago-icons-svg/btn-chevron-right.svg', import.meta.url).href}></lib-icon>`;
    const leadingActionIcon = this.actionIconTrailing ? nothing : actionIcon;
    const trailingActionIcon = this.actionIconTrailing ? actionIcon : nothing;
    return html`
      <div class="lib-inline-notification-icon" aria-hidden="true">
        <lib-icon class="glyph" icon-url=${this.iconUrl || new URL(`../wago-icons-svg/${icon}.svg`, import.meta.url).href}
                  ?spinning=${this.spinning || icon === 'spinner'}></lib-icon>
      </div>
      <div class="lib-inline-notification-container">
        <div class="lib-inline-notification-text">
          ${this.headline ? html`<div class="lib-inline-notification-headline">${this.headline}</div>` : nothing}
          <div class="lib-inline-notification-label"><slot>${this.text}</slot></div>
        </div>
        <div class="lib-inline-notification-action" ?hidden=${!this.hasSlottedAction && !this.actionText}>
          <slot name="action" @slotchange=${(event: Event) => { this.hasSlottedAction = (event.target as HTMLSlotElement).assignedElements().length > 0; }}>${this.actionText ? html`
            <lib-button variant=${this.actionVariant} ?full-width=${this.actionVariant !== 'tertiary'} @click=${() => this.emit('lib-action')}>
              ${leadingActionIcon}
              <span>${this.actionText}</span>
              ${trailingActionIcon}
            </lib-button>` : nothing}</slot>
        </div>
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lib-inline-notification': LibInlineNotification;
  }
}
